import Link from 'next/link'

/** Minimal serializable hub reference — just what the chip renders. */
export interface TopicHubLink {
  title: string
  slug: string
}

/**
 * "Featured in topic hubs" backlink for a glossary term — the reverse of the
 * topic registry's curated `glossaryTerms`. Closes the internal-link loop
 * (hub → term AND term → hub) so high-impression glossary pages pass authority
 * back to the hubs.
 *
 * Purely presentational: callers resolve the hubs SERVER-side (via
 * `getResourceTopicsForGlossaryTerm`) and pass the lightweight `{title, slug}[]`
 * here. This keeps the full `RESOURCE_TOPICS` registry (including hub overview /
 * FAQ copy) OUT of the glossary client bundle — it would otherwise be pulled in
 * by the hydrated `GlossaryTermLayout` just to render a few chips. Shared by both
 * render paths (crawler/no-JS `GlossaryTermServerContent` with `variant="section"`
 * and the hydrated sidebar with `variant="card"`). Returns null when empty.
 *
 * @param {{ hubs: readonly TopicHubLink[]; variant?: 'section' | 'card' }} props - Resolved hub links + layout variant.
 * @returns {JSX.Element | null} Hub backlinks, or null when none apply.
 */
export function GlossaryTopicHubs({
  hubs,
  variant = 'section',
}: {
  hubs: readonly TopicHubLink[]
  variant?: 'section' | 'card'
}): JSX.Element | null {
  if (hubs.length === 0) return null

  const chips = (
    <div className="flex flex-wrap gap-2">
      {hubs.map((hub) => (
        <Link
          key={hub.slug}
          href={`/resources/topics/${hub.slug}`}
          className="inline-flex items-center rounded-full border border-gray-200 bg-white px-3.5 py-1.5 text-sm font-medium text-gray-700 transition-colors hover:border-indigo-300 hover:bg-indigo-50 hover:text-indigo-700 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-200 dark:hover:border-indigo-500 dark:hover:bg-gray-800 dark:hover:text-indigo-300"
        >
          {hub.title}
        </Link>
      ))}
    </div>
  )

  if (variant === 'card') {
    return (
      <div className="rounded-xl border border-gray-200 bg-white p-6 dark:border-gray-800 dark:bg-gray-900">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Featured in topic hubs</h3>
        {chips}
      </div>
    )
  }

  return (
    <section className="mb-8">
      <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Featured in topic hubs</h2>
      <p className="text-gray-600 dark:text-gray-400 mb-4">
        Explore this term in context — alongside the related metrics, calculators, and guides curated in these hubs.
      </p>
      {chips}
    </section>
  )
}
